import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env';

import { AuditComment, AuditCommentsResponse } from '../../../interfaces/audit-comment.interface';
import {
  CfeTariffList,
  CreateQuotePayload,
  GetQuotesParams,
  PatchQuotePayload,
  PostalCodeResponse,
  Quote,
  QuoteLeadRef,
  QuoteLock,
  QuoteOptions,
  QuoteOptionsLocation,
  QuotesResponse,
  UtilityBillConfirmation,
  UtilityBillUpload,
  UtilityBillUploadPayload,
} from '../../../interfaces/quote.interface';
import { EntityTag } from '../../../interfaces/tag.interface';
import { QuotesApiService } from './quotes-api.service';

/**
 * Cliente de la API de cotizaciones. Los filtros viajan como `filter[x]`, los
 * multivalor (`status`, `scheme`, `tariff_code`, `state`) unidos por coma.
 */
@Injectable()
export class QuotesHttpService extends QuotesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.salesApiGateway}quotes`;

  list(filters: GetQuotesParams): Observable<QuotesResponse> {
    const { sort, page, ...rest } = filters;
    let params = new HttpParams();

    for (const [key, value] of Object.entries(rest)) {
      const text = Array.isArray(value) ? value.join(',') : value;
      if (text !== undefined && text !== null && text !== '') {
        params = params.set(`filter[${key}]`, String(text));
      }
    }
    if (sort) {
      params = params.set('sort', sort);
    }
    if (page) {
      params = params.set('page', page);
    }
    return this.http.get<QuotesResponse>(this.baseUrl, { params });
  }

  get(id: number): Observable<Quote> {
    return this.http.get<Quote>(`${this.baseUrl}/${id}`);
  }

  create(body: CreateQuotePayload): Observable<Quote> {
    return this.http.post<Quote>(this.baseUrl, body);
  }

  patch(id: number, sections: PatchQuotePayload): Observable<Quote> {
    return this.http.patch<Quote>(`${this.baseUrl}/${id}`, sections);
  }

  acquireLock(id: number): Observable<QuoteLock> {
    return this.http.post<QuoteLock>(`${this.baseUrl}/${id}/lock`, {});
  }

  releaseLock(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}/lock`);
  }

  options(location: QuoteOptionsLocation = {}): Observable<QuoteOptions> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(location)) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(`filter[${key}]`, String(value));
      }
    }
    return this.http.get<QuoteOptions>(`${this.baseUrl}/options`, { params });
  }

  postalCode(zip: string): Observable<PostalCodeResponse> {
    return this.http.get<PostalCodeResponse>(
      `${environment.salesApiGateway}quotes/postal-codes/${encodeURIComponent(zip)}`,
    );
  }

  cfeTariffs(period?: string): Observable<CfeTariffList> {
    const params = period ? new HttpParams().set('filter[period]', period) : undefined;
    return this.http.get<CfeTariffList>(`${this.baseUrl}/cfe-tariffs`, { params });
  }

  uploadUtilityBill(id: number, body: UtilityBillUploadPayload): Observable<UtilityBillUpload> {
    return this.http.post<UtilityBillUpload>(`${this.baseUrl}/${id}/utility-bill`, body);
  }

  confirmUtilityBill(id: number, fileId: number): Observable<UtilityBillConfirmation> {
    return this.http.post<UtilityBillConfirmation>(
      `${this.baseUrl}/${id}/utility-bill/${fileId}/confirm`,
      {},
    );
  }

  deleteUtilityBill(id: number, fileId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}/utility-bill/${fileId}`);
  }

  linkLead(id: number, leadId: number): Observable<{ lead: QuoteLeadRef }> {
    return this.http.post<{ lead: QuoteLeadRef }>(`${this.baseUrl}/${id}/lead`, {
      lead_id: leadId,
    });
  }
  setCloseProbability(id: number, pct: number, comment: string): Observable<Quote> {
    return this.http.post<Quote>(`${this.baseUrl}/${id}/close-probability`, { pct, comment });
  }

  setTags(id: number, tagIds: number[]): Observable<{ tags: EntityTag[] }> {
    return this.http.put<{ tags: EntityTag[] }>(`${this.baseUrl}/${id}/tags`, { tag_ids: tagIds });
  }

  listComments(id: number, before?: number): Observable<AuditCommentsResponse> {
    return this.http.get<AuditCommentsResponse>(`${this.baseUrl}/${id}/comments`, {
      params: before ? { before } : {},
    });
  }

  addComment(id: number, comment: string): Observable<AuditComment> {
    return this.http.post<AuditComment>(`${this.baseUrl}/${id}/comments`, { comment });
  }
}
